import { Button } from 'components/universal/button';
import Heading from 'components/universal/heading';
import Paragraph from 'components/universal/paragraph';
import React, { Fragment } from 'react';
import { Course } from 'types/elearning';
import cls from './overview.module.scss';
import { ReactComponent as LightbulbSvg } from 'assets/images/elearning/progress/lighbulb.svg';
import { ReactComponent as DocSvg } from 'assets/images/elearning/progress/doc.svg';
import { ReactComponent as QuestionBubbleSvg } from 'assets/images/elearning/progress/question-bubble.svg';
import { ReactComponent as CertificateSvg } from 'assets/images/elearning/progress/certificate.svg';
import { Circle } from './circle';
import { Certificate } from './certificate';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'constants/routes';
import { useDownloadCertificate } from 'hooks/useDownloadCertificate';
import { ReactComponent as NewIcon } from 'assets/images/elearning/icon-new.svg';
import { ReactComponent as UpdateIcon } from 'assets/images/elearning/icon-updated.svg';

interface IProps {
    courseData: Course;
    continueCourse: (course: Course) => void;
    type?: 'list' | 'single';
}

export const Overview = ({ courseData, continueCourse, type = 'single' }: IProps) => {
    const { t } = useTranslation('', {
        keyPrefix: 'elearning.handHygiene.courseLayout.overview',
    });
    const mapTextWithNewline = (text: string) =>
        text.split('\\n').map((t) => (
            <Fragment key={t}>
                <Paragraph className={cls.desc}>{t}</Paragraph>
            </Fragment>
        ));
    const courseProgress = courseData.stats.progress;
    const navigate = useNavigate();
    const { downloadCertificate } = useDownloadCertificate();
    const isCourseFinished = courseProgress === 100;
    const btnText = () => {
        if (courseProgress === 0) return t('startTraining');
        return isCourseFinished ? t('trainingFinished') : t('continueTraining');
    };

    return (
        <div className={type === 'list' ? cls.containerList : cls.container}>
            <div className={cls.wrapper}>
                <div className={cls.left}>
                    <p className={cls.labelAdded}>{courseData.is_updated ? t('updateLabel') : t('addedLabel') }: {courseData.date}</p>
                    <Heading headingLevel="h1" className={cls.header}>
                        {courseData.name}
                        {(courseData.is_new && courseProgress === 0 && !courseData.is_updated) && <p className={cls.isNew}><NewIcon />{t('newLabel')}</p>}
                        {courseData.is_updated && <p className={`${cls.isNew} ${cls.isUpdated}`}><UpdateIcon />{t('updatedLabel')}</p>}
                    </Heading>
                    {mapTextWithNewline(courseData.description)}
                    <div className={cls.buttonsWrapper}>
                        {(isCourseFinished && type === 'list') && (
                            <Button
                                className={cls.download}
                                onClick={() => downloadCertificate(courseData.path)}
                                styleType='green'
                            >
                                {t('downloadCertificateList')}
                            </Button>
                        )}
                        <Button
                            className={cls.btn}
                            onClick={() => {
                                if (type === 'single') {
                                    continueCourse(courseData);
                                } else {
                                    navigate(`${PATHS.ONLINE_TRAININGS}/${courseData.path}`);
                                }
                            }}
                            styleType={isCourseFinished ? 'border' : 'primary'}
                        >
                            {btnText()}
                        </Button>
                        
                   </div>
                </div>
                <div className={cls.right}>
                    <div className={cls.progress}>
                        <p className={cls.text}>
                            <b>{t('trainingProgress')}</b>
                        </p>
                        <div className={cls.circle}>
                            <Circle courseProgress={courseProgress} />
                        </div>
                    </div>
                    <div className={cls.details}>
                        <p className={cls.text}>
                            <b>{t('trainingDetails')}</b>
                        </p>
                        <div className={cls.row}>
                            <LightbulbSvg className={cls.icon} />
                            <p className={cls.text}>
                                {t('trainingIssues')}{' '}
                                <b>{courseData.stats.issues}</b>
                            </p>
                        </div>
                        <div className={cls.row}>
                            <DocSvg className={cls.icon} />
                            <p className={cls.text}>
                                {t('trainingTopics')}{' '}
                                <b>{courseData.stats.topics}</b>
                            </p>
                        </div>
                        <div className={cls.row}>
                            <QuestionBubbleSvg className={cls.icon} />
                            <p className={cls.text}>
                                {t('trainingQuizs')}{' '}
                                <b>{courseData.stats.quizzes}</b>
                            </p>
                        </div>
                        <div className={cls.row}>
                            <CertificateSvg className={cls.icon} />
                            <p className={cls.text}>
                                <b>{t('trainingCertificates')}</b>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {(type !== 'list') && (
                <p className={cls.description}>{t('description')}</p>
            )}
            
            {(isCourseFinished && type !== 'list') && (
                <div className={cls.certWrapper}>
                    <Certificate />
                </div>
            )}
        </div>
    );
};
